
const escapeDelimiter = (delimiter: string): string => delimiter === ' ' && '\\s' || delimiter;

const RegexAcceptLang = (delimiter: string) =>
  new RegExp(
    `((([a-zA-Z]+(-[a-zA-Z0-9]+){0,2})|\\*)(${delimiter}q=[0-1](\\.[0-9]+)?)?)*`,
    'g'
  );

export interface Locale {
  code: string;
  script?: string;
  region?: string;
  quality: number;
}

export interface PickOptions {
  loose?: boolean;
  delimiter?: string;
}

const DefaultDelimiter = ';';

const isString = (s: any): s is string => typeof s === 'string';

const isStringArray = (s: any): s is string[] => Array.isArray(s) && (s.length === 0 || isString(s[0]));

const mapToLocale = (lang: string, delimiter: string): Locale => {
  const bits = lang.split(delimiter);
  const ietf = bits[0].split('-');
  const hasScript = ietf.length === 3;

  return {
    code: ietf[0],
    ...(hasScript && { script:  ietf[1], region: ietf[2] } || {} ),
    ...(ietf.length === 2 && { region: ietf[1] } || { }),
    quality: bits[1] ? parseFloat(bits[1].split('=')[1]) : 1.0
  };
};

export const  parse = (acceptLanguage: string, delimiter: string = DefaultDelimiter): Locale[] => {
  const strings = (acceptLanguage || '').match(RegexAcceptLang(escapeDelimiter(delimiter)));
  if (!strings) {
    return [];
  }
  return strings.map((lang) => lang && mapToLocale(lang, delimiter))
    .filter((r): r is Locale => !!r)
    .sort((a, b) => b.quality - a.quality);
};

export const getLanguage = (l: Locale) => {
  if (l.region && l.code) {
    return `${l.code}-${l.region}`
  }
  return l.code;
}

export const pick = (supportedLanguages: string | string[],
                     acceptLanguage: string | string[] | Locale[],
                     options?: PickOptions): string | null => {
  options = options || {
    loose: true
  };

  if (!supportedLanguages || !supportedLanguages.length || !acceptLanguage) {
    return null;
  }

  const delimiter = options.delimiter || DefaultDelimiter;

  if (isString(acceptLanguage)){
    acceptLanguage = parse(acceptLanguage);
  } else if (isStringArray(acceptLanguage)){
    acceptLanguage = acceptLanguage.map(accept => mapToLocale(accept, delimiter));
  }

  const supported = isString(supportedLanguages) ? parse(supportedLanguages)
    : supportedLanguages.map((support) => mapToLocale(support, delimiter));

  for (const lang of acceptLanguage) {
    const langCode = lang.code.toLowerCase();
    const langRegion = lang.region ? lang.region.toLowerCase() : lang.region;
    const langScript = lang.script ? lang.script.toLowerCase() : lang.script;
    for (let j = 0; j < supported.length; j++) {
      const supportedCode = supported[j].code.toLowerCase();
      const supportedScript = supported[j].script && supported[j].script?.toLowerCase() || supported[j].script;
      const supportedRegion = supported[j].region && supported[j].region?.toLowerCase() || supported[j].region;
      if (langCode === supportedCode &&
        (options.loose || !langScript || langScript === supportedScript) &&
        (options.loose  || !langRegion || langRegion === supportedRegion)) {
        return supportedLanguages[j];
      }
    }
  }

  return null;
};
